import React, { useCallback, useContext, useEffect, useMemo, useReducer } from 'react'
import { reducer, initialState, actionTypes } from '@/components/report-tables/editable/reducer'
import { openErrorDialog } from '@/utils/dialogHelpers'
import { getCellMetadataFromReportSubmissionData } from '@/store/utils/reportUtils'
import { getStore } from '@/store'
import moment from 'moment'

const TableContext = React.createContext({ state: null,
  saveDataCells: null,
  changeEditorMode: null,
  runCheckingRules: null,
  hasCheckingRules: false,
  clearReportData: null
})

const useTableContext = () => useContext(TableContext)

const EditableTableContext = React.memo(({
  apiMethods = {
    saveDataCells: (changeArray, reportId) => new Promise(resolve => {}),
    getRecalculatedCells: (reportId, reportTemplateId) => new Promise(resolve => {}),
    runCheckingRules: (reportGroupId) => new Promise(resolve => {}),
    clearReportData: () => new Promise(resolve => {})
  },
  reportTable,
  getSourceData,
  sourceDataDispatch,
  submissionStatusUpdatedDispatch,
  submissionGroupStatusUpdatedDispatch,
  reportSubmissonTableIsReadOnly,
  ...props
}) => {
  if (!reportTable) return null

  const [state, dispatch] = useReducer(reducer, { ...initialState })
  const { recalculateCells, lastChangeTime } = state

  const { id, reportInstanceGroupId, reportSubmissionGroupId, anyCheckingRules } = reportTable
  const reportGroupId = reportInstanceGroupId || reportSubmissionGroupId

  const hasCheckingRules = useMemo(() => apiMethods.runCheckingRules && anyCheckingRules,
    [apiMethods.runCheckingRules, anyCheckingRules])

  const checkStatusChange = useCallback((currentData) => {
    if (submissionStatusUpdatedDispatch && reportTable.state !== currentData.state) {
      getStore().dispatch({ type: submissionStatusUpdatedDispatch, data: currentData.state })
    }
    if (submissionGroupStatusUpdatedDispatch && reportTable.reportSubmissionGroupState !== currentData.reportSubmissionGroupState) {
      getStore().dispatch({ type: submissionGroupStatusUpdatedDispatch, data: currentData.reportSubmissionGroupState })
    }
  }, [submissionStatusUpdatedDispatch, reportTable.state, reportTable.reportSubmissionGroupState, submissionGroupStatusUpdatedDispatch])

  useEffect(() => {
    if (!recalculateCells) return

    const checkIdleTime = () => {
      const now = moment()
      const duration = moment.duration(now.diff(lastChangeTime))

      if (duration.asSeconds() > 10) dispatch({ type: actionTypes.StopRecalculation })
    }

    const timeout = setInterval(checkIdleTime, 2000)

    return () => clearTimeout(timeout)
  }, [recalculateCells, lastChangeTime])

  useEffect(() => {
    if (!recalculateCells) return

    let sourceDataRecalculating = false

    const recalculate = () => {
      if (typeof apiMethods.getRecalculatedCells === 'function') {
        if (sourceDataRecalculating) return

        sourceDataRecalculating = true

        const reportId = id
        const reportTemplateId = reportTable.reportTemplateVersionId
        const existingData = getSourceData()

        apiMethods.getRecalculatedCells(reportId, reportTemplateId, true)
          .then(response => {
            const cellMetadata = getCellMetadataFromReportSubmissionData(response, existingData.cellMetadata)

            getStore().dispatch({
              type: sourceDataDispatch,
              data: {
                data: response,
                mergeData: true,
                cellMetadata: cellMetadata
              }
            })
            sourceDataRecalculating = false
          })
          .catch(() => {
            openErrorDialog({})
            sourceDataRecalculating = false
          })
      }
    }

    const timeout = setInterval(recalculate, 5000)

    return () => clearInterval(timeout)
    // Disabling exhaustive deps check - we only need the handler in useEffect to get registered once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recalculateCells])

  const saveDataCells = useCallback((changes) => {
    if (!changes || !changes.length) return
    let sourceData = getSourceData().sourceData

    changes.forEach(change => {
      const alreadyExists = sourceData.find(x => x.columnTreeCode === change.columnTreeCode && x.rowTreeCode === change.rowTreeCode)

      sourceData = alreadyExists
        ? sourceData.map(x => x.columnTreeCode === change.columnTreeCode && x.rowTreeCode === change.rowTreeCode ? { ...x, ...change } : x)
        : [sourceData, { ...change }]
    })

    dispatch({ type: actionTypes.SourceDataSaving })
    getStore().dispatch({ type: sourceDataDispatch, data: { data: sourceData } })

    apiMethods.saveDataCells([...changes], id)
      .then(response => {
        checkStatusChange(response)
        dispatch({ type: actionTypes.SourceDataSaved })
      })
      .catch(() => {
        openErrorDialog({ customTitle: 'Reporting.SaveDataCellsErrorTitle',
          customErrorText: 'Reporting.SaveDataCellsErrorText'
        })

        dispatch({ type: actionTypes.SourceDataSavingError })
      })
  }, [apiMethods, checkStatusChange, getSourceData, id, sourceDataDispatch])

  const changeEditorMode = useCallback((change) => {
    dispatch({ type: actionTypes.ChangeEditorMode,
      data: {
        isEditorOpen: change.editorMode === 'none',
        editorMode: change.editorMode
      }
    })
  }, [])

  const runCheckingRules = useCallback(() => {
    return apiMethods.runCheckingRules(reportGroupId)
  }, [apiMethods, reportGroupId])

  const clearReportData = useCallback(() => {
    dispatch({ type: actionTypes.SourceDataSaving })
    getStore().dispatch({
      type: reportSubmissonTableIsReadOnly,
      newValue: true
    })

    apiMethods.clearReportData(id)
      .then(response => {
        const cellMetadata = getCellMetadataFromReportSubmissionData(response)
        getStore().dispatch({
          type: sourceDataDispatch,
          data: {
            data: response,
            cellMetadata: cellMetadata
          }
        })
        dispatch({ type: actionTypes.SourceDataSaved })
      })
      .finally(() => {
        getStore().dispatch({
          type: reportSubmissonTableIsReadOnly,
          newValue: false
        })
      }).catch(err => {
        const error = err && err.response && err.response.data && err.response.data.error
        openErrorDialog({ customTitle: 'Reporting.ClearDataFailed', customErrorText: error.message })
      })
  }, [apiMethods, id, reportSubmissonTableIsReadOnly, sourceDataDispatch])

  return <TableContext.Provider value={{ state,
    saveDataCells,
    changeEditorMode,
    runCheckingRules,
    hasCheckingRules,
    clearReportData
  }}>
    { props.children }
  </TableContext.Provider>
})

export { EditableTableContext, useTableContext }



// WEBPACK FOOTER //
// ./src/components/report-tables/editable/editableTableContext.js